import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
// import { feeGroup, feesTypes, paymentType } from '../../../core/common/selectoption/selectoption'
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import { status } from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useRegistrationFeeConfig } from "../hooks/useRegistrationFeeConfig";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useFeeType } from "../../../feetypes/hooks/useFeeType";
import { showToast } from "../../../../core/utils/alert";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const AddRegistrationFeeConfig = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const {
    addFeeConfig,
    updateFeeConfig,
    useGetFeeConfigById,
    isAdding,
    isUpdating,
  } = useRegistrationFeeConfig();
  const { id } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { feeConfig, loading, error } = useGetFeeConfigById(id || "");

  const [formData, setFormData] = useState<{
    course_id: string;
    type_id: string;
    fee: number;
  }>({
    course_id: "",
    type_id: "",
    fee: 0,
  });
  const { getCourseState } = useCourse();
  const { courses } = getCourseState();

  const { getFeeTypeState } = useFeeType();
  const { feetypes } = getFeeTypeState();

  // Filter out the "Course Fee" from the fee types
  const filteredFeeTypes = feetypes.filter(
    (feeType) => feeType.type !== "coursefee"
  );

  // Mapping for fee type display labels
  const feeTypeMapping: { [key: string]: string } = {
    registrationfee: "Registration Fee",
    busfee: "Bus Fee",
    libraryfee: "Library Fee",
    // Add other mappings as needed
  };

  // console.log(filteredFeeTypes, 'ft')

  const validationSchema = yup.object().shape({
    course_id: yup.string().required("Course is required"),
    type_id: yup.string().required("Fee type is required"),
    fee: yup
      .number()
      .required("Fee amount is required")
      .min(0, "Fee must be a positive number"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: formData,
  });

  useEffect(() => {
    if (id && feeConfig) {
      setFormData({
        course_id: feeConfig.registrationfee.course.id.toString(),
        type_id: feeConfig.registrationfee.feeType.id.toString(),
        fee: parseFloat(feeConfig.registrationfee.fee),
      });
      setValue("course_id", feeConfig.registrationfee.course.id.toString());
      setValue("type_id", feeConfig.registrationfee.feeType.id.toString());
      // setValue(
      //   "effective_date",
      //   new Date(feeConfig.registrationfee.effective_date)
      // );
      setValue("fee", parseFloat(feeConfig.registrationfee.fee));
      setIsEdit(true);
    } else {
      setIsEdit(false);
      setFormData({
        course_id: "",
        type_id: "",
        fee: 0,
      });
    }
  }, [id, feeConfig, setValue]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // console.log(errors)
  const onSubmit = async (data: any) => {
    const payload = {
      course_id: Number(data.course_id),
      type_id: Number(data.type_id),
      fee: data.fee,
    };

    let result;
    if (id) {
      result = await updateFeeConfig(id, payload);
    } else {
      result = await addFeeConfig(payload);
    }
    if (result.success) {
      showToast({
        title: "Success",
        text: id
          ? "Registration Fee updated successfully."
          : "Registration Fee added successfully.",
        icon: "success",
        timer: 5000,
      });
      navigate(routes.courseRegistrationFee);
    } else {
      showToast({
        title: "Error",
        text: id
          ? "Failed to update registration fee."
          : "Failed to add registration fee.",
        icon: "error",
        timer: 5000,
      });
    }
  };

  // if (loading) {
  //   return <div>Loading...</div>; // Show a loading spinner or message
  // }

  // if (error) {
  //   return <div>Error loading fee configuration</div>; // Show an error message
  // }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Updated Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEdit ? "Edit" : "Add"} Registration Fee
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.courseRegistrationFee}>
                      Course Registration Fee
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Registration Fee
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* FeeConfig Core Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-calendar-event fs-16" />
                      </span>
                      <h4 className="text-dark">Registration Fee Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Course <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="course_id"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className="select"
                                options={[
                                  { value: "", label: "Select a Course" },
                                  ...courses.map((course) => ({
                                    value: String(course.id),
                                    label: course.name,
                                  })),
                                ]}
                                value={
                                  formData.course_id
                                    ? {
                                        value: formData.course_id,
                                        label:
                                          courses.find(
                                            (course) =>
                                              course.id ===
                                              Number(formData.course_id)
                                          )?.name || "",
                                      }
                                    : undefined
                                }
                                onChange={(selectedOption) => {
                                  handleInputChange(
                                    "course_id",
                                    selectedOption?.value
                                  );
                                  field.onChange(selectedOption?.value);
                                }}
                              />
                            )}
                          />
                          {errors.course_id && (
                            <div className="invalid-feedback">
                              {errors.course_id.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Fee Type <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="type_id"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className="select"
                                options={[
                                  { value: "", label: "Select a Type" },
                                  ...filteredFeeTypes.map((feeType) => ({
                                    value: String(feeType.id),
                                    label:
                                      feeTypeMapping[feeType.type] ||
                                      feeType.type,
                                  })),
                                ]}
                                value={
                                  formData.type_id
                                    ? {
                                        value: formData.type_id,
                                        label: (() => {
                                          const foundFeeType =
                                            filteredFeeTypes.find(
                                              (feeType) =>
                                                feeType.id ===
                                                Number(formData.type_id)
                                            );
                                          return foundFeeType
                                            ? feeTypeMapping[
                                                foundFeeType.type
                                              ] || ""
                                            : "";
                                        })(),
                                      }
                                    : undefined
                                }
                                onChange={(selectedOption) => {
                                  // handleFeeTypeChange(selectedOption);
                                  field.onChange(selectedOption?.value);
                                }}
                              />
                            )}
                          />
                          {errors.type_id && (
                            <div className="invalid-feedback">
                              {errors.type_id.message}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Status <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className="select"
                                options={[
                                  { value: "active", label: "Active" },
                                  { value: "inactive", label: "Inactive" },
                                ]}
                                value={
                                  status.find(
                                    (option) => option.value === formData.status
                                  )
                                    ? {
                                        value: String(formData.status),
                                        label:
                                          status.find(
                                            (status) =>
                                              status.value === formData.status
                                          )?.label || "",
                                      }
                                    : undefined
                                }
                                onChange={(selectedOption) => {
                                  handleInputChange(
                                    "status",
                                    selectedOption?.value
                                  );
                                  field.onChange(selectedOption?.value);
                                }}
                              />
                            )}
                          />
                          {errors.status && (
                            <div className="invalid-feedback">
                              {errors.status.message}
                            </div>
                          )}
                        </div>
                      </div> */}
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Fee Amount</label>
                          <Controller
                            name="fee"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="number"
                                className={`form-control ${
                                  errors.fee ? "is-invalid" : ""
                                }`}
                                placeholder="Enter Fee amount"
                                value={field.value}
                                onChange={(e) => {
                                  const value = Math.max(
                                    0,
                                    parseFloat(e.target.value) || 0
                                  );
                                  field.onChange(value);
                                  handleInputChange("fee", value);
                                }}
                              />
                            )}
                          />
                          {errors.fee && (
                            <div className="invalid-feedback">
                              {errors.fee.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-end mt-4">
                  <Link
                    to={routes.courseRegistrationFee}
                    className="btn btn-light me-3"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isAdding || isUpdating}
                  >
                    {isAdding
                      ? "Saving..."
                      : isUpdating
                      ? "Updating..."
                      : isEdit
                      ? "Update FeeConfig"
                      : "Create FeeConfig"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default AddRegistrationFeeConfig;
